<template>
  <cs-preloader-overlay :loading="loading">
    <div class="main-screen" :style="{ backgroundImage }">
      <div class="main-screen-container">
        <div class="title-container">
          <capusta-title :title="$t('timezone.title')" icon-outside>
            <template slot="description">
              <div class="main-description" v-html="$t('timezone.description')" />
            </template>
          </capusta-title>
        </div>
        <div class="content-container">
          <substrate-mount>
            <simple-button
              size="small"
              variant="darken"
              @click="handleShownTimezoneAutomaticDetection"
            >
              {{ $t('timezone.automatic.detection.button') }}
            </simple-button>
            <div class="option-container">
              <option-panel
                :option="formattedTimezoneArea"
                noactive
                @click="handleShownTimezoneAreaList"
              >
                <cs-button type="secondary">
                  {{ $t('common.change') }}
                </cs-button>
              </option-panel>
            </div>
            <div class="option-container">
              <option-panel
                :option="formattedTimeToUTCWithCity"
                noactive
                @click="handleShownCurrentAreaTimezoneList"
              >
                <cs-button type="secondary">
                  {{ $t('common.change') }}
                </cs-button>
              </option-panel>
            </div>
            <div class="text-container">
              {{ formattedDateTimeWithTimezone }}
            </div>
          </substrate-mount>
        </div>
      </div>
    </div>

    <timezone-area-dialog
      @change="updateMerchantTimezone"
    />

    <timezone-zones-dialog
      @change="updateMerchantTimezone"
    />

    <timezone-automatic-detection-dialog
      @change="updateMerchantTimezone"
    />
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

import { getDatetimeWithTimezone } from 'lib/utils/date';

import HTTPService from '@/services/Http';

import CapustaTitle from 'modules/CapustaLayout/CapustaTitle';
import SubstrateMount from '@/components/capusta/SubstrateMount';
import OptionPanel from '@/components/capusta/OptionPanel';

import SimpleButton from 'molecules/common/SimpleButton';

import TimezoneAreaDialog from 'organisms/timezone/TimezoneAreaDialog';
import TimezoneZonesDialog from 'organisms/timezone/TimezoneAreaZonesDialog';
import TimezoneAutomaticDetectionDialog from 'organisms/timezone/TimezoneAutomaticDetectionDialog';

import {
  DATE_FORMAT,
  TIME_FORMAT,
  OFFSET_TIME_FORMAT
} from 'definitions/dates';

import { EVENT_BUS } from '@/eventBus';

export default {
  name: 'TimezoneView',
  components: {
    CapustaTitle,
    SubstrateMount,
    OptionPanel,
    SimpleButton,
    TimezoneAreaDialog,
    TimezoneZonesDialog,
    TimezoneAutomaticDetectionDialog,
  },
  created() {
    this.initialize();
  },
  data() {
    return {
      loading: false,
      timer: null,
    };
  },
  computed: {
    backgroundImage() {
      return `url(${require('@/assets/svg/timezone/timezone-inactive.svg')})`;
    },
    merchantTimezoneCode() {
      return this.getMerchantTimezone.code;
    },
    formattedOffsetTimeToUTC() {
      const offsetTimeToUTC = getDatetimeWithTimezone(new Date, this.merchantTimezoneCode)
        .format(OFFSET_TIME_FORMAT);

      return `UTC ${offsetTimeToUTC}`;
    },
    formattedDateTimeWithTimezone() {
      const formattedDateWithTimezone = getDatetimeWithTimezone(new Date, this.merchantTimezoneCode)?.format(DATE_FORMAT);

      const formattedTimeWithTimezone = this.timer?.format(TIME_FORMAT);

      return `${formattedDateWithTimezone} / ${formattedTimeWithTimezone}`;
    },
    formattedTimezoneArea() {
      return {
        description: this.getCurrentTimezoneAreaCode,
        label: this.$t('timezone.area'),
      };
    },
    formattedTimeToUTCWithCity() {
      const slashSymbolIndex = this.merchantTimezoneCode.indexOf('/');
      const slicedTimezoneCode = this.merchantTimezoneCode.slice(slashSymbolIndex + 1);

      return {
        description: `${this.formattedOffsetTimeToUTC} / ${slicedTimezoneCode}`,
        label: this.$t('timezone.city'),
      };
    },
    ...mapState(['MERCHANT_INFO']),
    ...mapGetters(['getMerchantTimezone']),

    ...mapState('timezone', ['currentTimezoneArea']),
    ...mapGetters('timezone', ['getCurrentTimezoneAreaCode']),
  },
  methods: {
    async initialize() {
      this.loading = true;

      try {
        await this.fetchTimezoneList();

        setInterval(this.updateTimer, 1000);
        this.updateTimer();

        this.loading = false;
      } catch (error) {
        this.$alert({
          type: 'warning',
          text: 'notification.timezone.error',
        });

        this.$router.push({ name: 'cabinet' });
      }
    },
    updateTimer() {
      this.timer = getDatetimeWithTimezone(new Date, this.merchantTimezoneCode);
    },
    handleShownTimezoneAreaList() {
      EVENT_BUS.$emit('EVENT_SHOW_TIMEZONE_AREA_LIST');
    },
    handleShownCurrentAreaTimezoneList() {
      EVENT_BUS.$emit('EVENT_SHOW_CURRENT_AREA_TIMEZONE_LIST');
    },
    handleShownTimezoneAutomaticDetection() {
      EVENT_BUS.$emit('EVENT_SHOW_TIMEZONE_AUTOMATIC_DETECTION');
    },
    async updateMerchantTimezone(timezone) {
      try {
        await HTTPService.updateMerchant({
          ...this.MERCHANT_INFO,
          timezone,
        });

        this.SET_MERCHANT_TIMEZONE(timezone);

        this.SET_CURRENT_TIMEZONE_AREA({
          active: false,
          code: timezone.code.split('/')[0],
          id: timezone.area_id,
        });

        this.$alert({
          type: 'golubok',
          text: 'notification.timezone.success',
        });
      } catch (error) {
        console.log(error);

        this.$alert({
          type: 'warning',
          text: 'error.in.system',
        });
      }
    },
    ...mapMutations([
      'SET_MERCHANT_TIMEZONE',
    ]),
    ...mapMutations('timezone', [
      'SET_CURRENT_TIMEZONE_AREA',
    ]),
    ...mapActions('timezone', [
      'fetchTimezoneList',
    ]),
  },
};
</script>

<style lang="sass" scoped>
  .main-screen
    width: 100%
    margin: 0 auto
    background-position: top 5px right -20px
    background-repeat: no-repeat
    background-size: 140px 140px

    +respond-to('small')
      max-width: 600px
      background-position: top 20px right 0px

    +respond-to('medium')
      max-width: 930px
      background-position: top 84px right 0
      background-size: 216px 216px

  .main-screen-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto
    padding: 20px 0px 40px
    overflow: hidden

    +respond-to('small')
      padding: 40px 0px
      overflow: visible

    +respond-to('medium')
      padding: 70px 0px

  .main-description
    width: 340px

    span
      display: block

      +respond-to('small')
        display: inline

  .title-container
    max-width: 460px
    position: relative
    margin: 0 20px
    padding: 0 0 20px

    +respond-to('medium')
      margin: 0 auto

  .content-container
    *
      &:not(:last-child)
        margin-bottom: 20px

  .text-container
    font-weight: 400
    font-size: 20px
    line-height: 40px
    color: #fff
</style>
